import { applyCommand, type Command } from "../command";
import { isFoundingSite } from "../court/king";
import { type AxialCoord, hexDistance, hexEquals, hexKey } from "../hex";
import { isWorkingCity, workingCities } from "../land/cities";
import { firstAccepted, rankBy } from "../pick";
import { findHex, mapOrder } from "../run/board";
import { kingOf, supplyOf } from "../run/pools";
import { isHeldUnit, kingSeatOf } from "../run/seats";
import { hostile, sideOf } from "../run/sides";
import type { CityCommandRules } from "../types/rules";
import type { FactionId, HexState, RunState, Unit } from "../types/state";
import { isBroken } from "../units/morale";
import { trainShortfall } from "../units/roster";
import { road, walledAgainst } from "../units/routes";
import { cityKind } from "./cityDuty";
import { cityColumn, cityLandOf, isCityCommand, isHandHeld } from "./cityVerbs";

/**
 * The City Command script (CC4): at most one command a turn through the human's door: Home, a free
 * city, a founding, a column at the rival's nearest city, the King to the forge.
 */

/**
 * Rival units standing on the land a city works (CC4): what the city script reads as an
 * invasion. Narrower than Home's guard, which answers a rival anywhere in the reach: one passing
 * on neutral or rival land does not pull a column home (the advice proxy reads it the same way).
 */
function intrudersOn(state: RunState, faction: FactionId, city: AxialCoord): number {
  const land = new Set(cityLandOf(state, faction, city).map((hex) => hexKey(hex.coord)));
  return state.units.filter(
    (unit) => hostile(state.rules, unit.owner, faction) && land.has(hexKey(unit.coord)),
  ).length;
}

/** Hexes on the road a city's soldiers take to `to` (CC4, the arrow's road); undefined when there is none. */
function roadSteps(
  state: RunState,
  faction: FactionId,
  from: AxialCoord,
  to: AxialCoord,
): number | undefined {
  const route = road(state.hexes, sideOf(state.rules, faction), from, to);
  return route && route.length - 1;
}

/** A soldier the city script counts on (CC4): its column's, unbroken and nobody's hand but the city's. */
function isFreeSoldier(state: RunState, unit: Unit): boolean {
  return !isBroken(state, unit) && !isHandHeld(state, unit) && !isHeldUnit(state, unit.id);
}

/** What the city script reads of its kingdom once a turn (CC4). */
interface CityScriptView {
  readonly faction: FactionId;
  readonly rules: CityCommandRules;
  readonly cities: readonly HexState[];
  /** Rival units on each city's land, by hex key. */
  readonly intruders: ReadonlyMap<string, number>;
  /** Cities with no errand of their own: Home or an arrow on land already theirs, and no rival on their land. */
  readonly ready: readonly HexState[];
  /** A hex's place on the map: the tie-break. */
  readonly order: (coord: AxialCoord) => number;
}

/** Rule 1 (CC4): a city whose land holds a rival unit while its arrow points away goes Home. */
function homeCommands(view: CityScriptView): Command[] {
  return view.cities
    .filter((city) => city.arrow !== undefined && (view.intruders.get(hexKey(city.coord)) ?? 0) > 0)
    .map((city) => ({ kind: "mark", at: city.coord, from: city.coord }));
}

/**
 * Rule 2 (CC4): a ready city with a free soldier points at a free (neutral) city no own arrow
 * targets, the nearest pair by road first.
 */
function freeCityCommands(state: RunState, view: CityScriptView): Command[] {
  const targeted = new Set(view.cities.flatMap((city) => (city.arrow ? [hexKey(city.arrow)] : [])));
  const free = state.hexes.filter(
    (hex) =>
      hex.owner === "none" &&
      hex.structure === "city" &&
      !hex.razed &&
      !targeted.has(hexKey(hex.coord)),
  );
  const pairs = view.ready
    .filter((city) => cityColumn(state, city.coord).some((unit) => isFreeSoldier(state, unit)))
    .flatMap((city) =>
      free.flatMap((target) => {
        const steps = roadSteps(state, view.faction, city.coord, target.coord);
        return steps === undefined ? [] : [{ city, target, steps }];
      }),
    );
  return rankBy(
    pairs,
    (pair) => pair.steps,
    (pair) => view.order(pair.city.coord),
    (pair) => view.order(pair.target.coord),
  ).map(({ city, target }) => ({ kind: "mark", at: target.coord, from: city.coord }));
}

/** Rule 3 (CC4): with a city's price and `foundMargin` in the pool and no errand, the King founds at the nearest site. */
function foundingCommands(state: RunState, view: CityScriptView): Command[] {
  const king = kingOf(state, view.faction);
  if (
    !king ||
    king.founds ||
    supplyOf(state, view.faction) < state.rules.cityCost + view.rules.foundMargin
  ) {
    return [];
  }
  return rankBy(
    state.hexes.filter((hex) => isFoundingSite(state, view.faction, hex.coord)),
    (hex) => hexDistance(hex.coord, king.coord),
    (hex) => view.order(hex.coord),
  ).map((hex) => ({ kind: "build", at: hex.coord, structure: "city" }));
}

/** A foreign hex behind walls: what a column needs a Siege for (CC4). */
function isWalledForeign(state: RunState, faction: FactionId, coord: AxialCoord): boolean {
  const hex = findHex(state, coord);
  return hex !== undefined && walledAgainst(hex, sideOf(state.rules, faction));
}

/**
 * Rule 4 (CC4): a ready city with a full column points at the rival's working city nearest it
 * by road; before walls, with no Siege in the column, it trains Siege first. A city making Siege
 * goes back to Auto once one is in its column, or once no walls stand before it (sent Home, its
 * target taken): measured, a city sent Home kept training Sieges, the slowest kind, into a
 * defence that wanted none.
 */
function attackCommands(state: RunState, view: CityScriptView): Command[] {
  const rivals = state.hexes.filter(
    (hex) => hostile(state.rules, hex.owner, view.faction) && isWorkingCity(hex, state.tick),
  );
  return view.cities.flatMap((city): Command[] => {
    const column = cityColumn(state, city.coord);
    const siege = column.some((unit) => unit.kind === "siege");
    const auto = (walled: boolean): Command[] =>
      city.makes === "siege" && (siege || !walled)
        ? [{ kind: "stance", city: city.coord, stance: "garrison" }]
        : [];
    if (!view.ready.includes(city) || column.length < state.rules.unitsPerCity) {
      return auto(city.arrow !== undefined && isWalledForeign(state, view.faction, city.arrow));
    }
    const [target] = rankBy(
      rivals.flatMap((hex) => {
        const steps = roadSteps(state, view.faction, city.coord, hex.coord);
        return steps === undefined ? [] : [{ hex, steps }];
      }),
      (candidate) => candidate.steps,
      (candidate) => view.order(candidate.hex.coord),
    );
    if (!target) {
      return auto(false);
    }
    const walled = target.hex.walls > 0;
    const siegeFirst: Command[] =
      walled && !siege && city.makes !== "siege"
        ? [{ kind: "train", city: city.coord, unit: "siege" }]
        : [];
    return [
      ...auto(walled),
      ...siegeFirst,
      { kind: "mark", at: target.hex.coord, from: city.coord },
    ];
  });
}

/**
 * Soldiers the forge would speed in a city (CC4): those its yard has still to start, while it
 * trains and the pool holds the price and the recruit of its kind; 0 otherwise.
 */
function forgeWork(state: RunState, faction: FactionId, city: HexState): number {
  const kind = cityKind(state, faction, city);
  if (!kind || trainShortfall(state, faction, kind) !== undefined) {
    return 0;
  }
  const training = city.unitBuild === "none" ? 0 : 1;
  return Math.max(0, state.rules.unitsPerCity - cityColumn(state, city.coord).length - training);
}

/**
 * Rule 5 (CC4): the King, with no founding errand, rides to the own city under the most threat
 * (rival units on its land), else to the one where the forge pays most; the city he stands on
 * wins ties, then the nearest. Nothing while he stands there or rides there already.
 */
function kingCommands(state: RunState, view: CityScriptView): Command[] {
  const king = kingOf(state, view.faction);
  if (!king || king.founds) {
    return [];
  }
  const best = (score: (city: HexState) => number): HexState | undefined =>
    rankBy(
      view.cities
        .map((city) => ({ city, score: score(city) }))
        .filter((candidate) => candidate.score > 0),
      (candidate) => -candidate.score,
      (candidate) => hexDistance(candidate.city.coord, king.coord),
      (candidate) => view.order(candidate.city.coord),
    )[0]?.city;
  const goal =
    best((city) => view.intruders.get(hexKey(city.coord)) ?? 0) ??
    best((city) => forgeWork(state, view.faction, city));
  if (
    !goal ||
    hexEquals(goal.coord, king.coord) ||
    (king.destination !== undefined && hexEquals(king.destination, goal.coord))
  ) {
    return [];
  }
  return [{ kind: "moveKing", to: goal.coord }];
}

/**
 * The city script's choice (CC4): the first command, by priority, that the city-command King's
 * door would apply, with the state it makes. Rules: (1) Home for a city invaded while its arrow
 * points away; (2) a free city; (3) a founding; (4) the rival's nearest city; (5) the King to his
 * work. Within a rule: nearest first, then map order. It reads the run in progress and nothing
 * else (no memory across runs), and never gives a hand order.
 */
function cityScriptChoice(
  state: RunState,
  faction: FactionId,
): { readonly command: Command; readonly next: RunState } | undefined {
  const rules = state.rules.cityCommand;
  const king = kingSeatOf(state, faction);
  if (
    !rules ||
    king === undefined ||
    state.status !== "running" ||
    !isCityCommand(state, faction)
  ) {
    return undefined;
  }
  const cities = workingCities(state, faction);
  const intruders = new Map(
    cities.map((city) => [hexKey(city.coord), intrudersOn(state, faction, city.coord)]),
  );
  const ready = cities.filter(
    (city) =>
      (intruders.get(hexKey(city.coord)) ?? 0) === 0 &&
      (city.arrow === undefined || findHex(state, city.arrow)?.owner === faction),
  );
  const view: CityScriptView = {
    faction,
    rules,
    cities,
    intruders,
    ready,
    order: mapOrder(state.hexes),
  };
  const priorities: readonly (() => Command[])[] = [
    () => homeCommands(view),
    () => freeCityCommands(state, view),
    () => foundingCommands(state, view),
    () => attackCommands(state, view),
    () => kingCommands(state, view),
  ];
  // The city-command King's own door, by his seat's number: the seat's check, then City
  // Command's meaning of the verb.
  for (const candidates of priorities) {
    const accepted = firstAccepted(state, candidates(), (from, command) =>
      applyCommand(from, king, command),
    );
    if (accepted) {
      return { command: accepted.chosen, next: accepted.next };
    }
  }
  return undefined;
}

/** The command the city script would give now (CC4); undefined when no rule finds one the door takes. */
export function scriptedCityCommand(state: RunState, faction: FactionId): Command | undefined {
  return cityScriptChoice(state, faction)?.command;
}

/**
 * A scripted city-command kingdom's turn (CC4, one rulebook): at most one command, the one
 * `scriptedCityCommand` names, through the same door and rules as the human King's. Called
 * from the tick each `scriptCommandTicks`; same object back when it gives none.
 */
export function scriptedCityTurn(state: RunState, faction: FactionId): RunState {
  return cityScriptChoice(state, faction)?.next ?? state;
}
